import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Slider,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import Cropper, { Area } from 'react-easy-crop';
import {
  ASPECT_RATIO_OPTIONS,
  AspectRatioChoice,
  NATURAL_ASPECT,
  createImageElement,
  getCroppedImageBlob,
  isFullImageCrop,
  resolveOutputMimeType,
  uploadImageBlob,
} from '../utils/imageUploadCore';

export interface ImageUploadCropDialogProps {
  open: boolean;
  file: File | null;
  onClose: () => void;
  onUploaded: (imageUrl: string) => void;
  maxSize?: number;
  forcedAspectRatio?: number;
  defaultAspectRatio?: number;
  hideAspectRatioSelector?: boolean;
}

const ImageUploadCropDialog: React.FC<ImageUploadCropDialogProps> = ({
  open,
  file,
  onClose,
  onUploaded,
  maxSize = 1,
  forcedAspectRatio,
  defaultAspectRatio,
  hideAspectRatioSelector = false,
}) => {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [selectedImageSrc, setSelectedImageSrc] = useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [selectedFileType, setSelectedFileType] = useState('image/jpeg');
  const [selectedAspectRatio, setSelectedAspectRatio] = useState<AspectRatioChoice>(NATURAL_ASPECT);
  const [naturalAspectRatio, setNaturalAspectRatio] = useState<number | null>(null);

  const effectiveAspectRatio =
    forcedAspectRatio ??
    (selectedAspectRatio === NATURAL_ASPECT
      ? naturalAspectRatio ?? 16 / 9
      : selectedAspectRatio);

  useEffect(() => {
    if (!open || !file) {
      setSelectedImageSrc(null);
      setNaturalAspectRatio(null);
      setCroppedAreaPixels(null);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setError('');
      return;
    }

    const localSrc = URL.createObjectURL(file);
    setSelectedImageSrc(localSrc);
    setSelectedFileType(resolveOutputMimeType(file));
    setSelectedAspectRatio(forcedAspectRatio ?? defaultAspectRatio ?? NATURAL_ASPECT);

    return () => {
      URL.revokeObjectURL(localSrc);
    };
  }, [open, file, forcedAspectRatio, defaultAspectRatio]);

  useEffect(() => {
    if (!selectedImageSrc) {
      setNaturalAspectRatio(null);
      return;
    }
    let cancelled = false;
    createImageElement(selectedImageSrc)
      .then((img) => {
        if (!cancelled && img.naturalWidth > 0 && img.naturalHeight > 0) {
          setNaturalAspectRatio(img.naturalWidth / img.naturalHeight);
        }
      })
      .catch(() => {
        if (!cancelled) setNaturalAspectRatio(null);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedImageSrc]);

  const handleClose = () => {
    if (uploading) return;
    onClose();
  };

  const handleApplyCropAndUpload = async () => {
    if (!file || !selectedImageSrc || !croppedAreaPixels) {
      handleClose();
      return;
    }

    setError('');
    setUploading(true);
    try {
      const image = await createImageElement(selectedImageSrc);
      const targetSizeBytes = maxSize * 1024 * 1024;
      const canUploadOriginal =
        file.size <= targetSizeBytes &&
        isFullImageCrop(croppedAreaPixels, image.naturalWidth, image.naturalHeight);

      const imageUrl = canUploadOriginal
        ? await uploadImageBlob(file, file.type)
        : await uploadImageBlob(
            await getCroppedImageBlob(
              selectedImageSrc,
              croppedAreaPixels,
              selectedFileType,
              maxSize
            ),
            selectedFileType
          );

      onUploaded(imageUrl);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Erreur lors de l'édition de l'image";
      setError(message);
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => {
        if (reason === 'backdropClick') return;
        handleClose();
      }}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>Recadrer l&apos;image</DialogTitle>
      <DialogContent>
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            height: 380,
            bgcolor: '#111',
            borderRadius: 1,
            overflow: 'hidden',
          }}
        >
          {selectedImageSrc && (
            <Cropper
              image={selectedImageSrc}
              crop={crop}
              zoom={zoom}
              aspect={effectiveAspectRatio}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={(_, croppedPixels) => setCroppedAreaPixels(croppedPixels)}
            />
          )}
        </Box>

        <Box sx={{ mt: 2 }}>
          {!hideAspectRatioSelector && !forcedAspectRatio && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" gutterBottom>
                Ratio
              </Typography>
              <ToggleButtonGroup
                value={selectedAspectRatio}
                exclusive
                size="small"
                onChange={(_, value: AspectRatioChoice | null) => {
                  if (!value) return;
                  setSelectedAspectRatio(value);
                }}
                sx={{ flexWrap: 'wrap', gap: 1 }}
              >
                <ToggleButton value={NATURAL_ASPECT} disabled={!naturalAspectRatio}>
                  Naturel
                </ToggleButton>
                {ASPECT_RATIO_OPTIONS.map((option) => (
                  <ToggleButton key={option.label} value={option.value}>
                    {option.label}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Box>
          )}

          <Typography variant="body2" gutterBottom>
            Zoom
          </Typography>
          <Slider
            value={zoom}
            min={1}
            max={3}
            step={0.1}
            onChange={(_, value) => setZoom(value as number)}
          />
          <Typography variant="caption" color="text.secondary">
            JPEG : qualité préservée si l&apos;image entière est conservée (ratio Naturel).
            Sinon compression max {maxSize} Mo après recadrage.
          </Typography>
          {error ? (
            <Typography variant="body2" color="error" sx={{ mt: 1 }}>
              {error}
            </Typography>
          ) : null}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={uploading}>
          Annuler
        </Button>
        <Button
          variant="contained"
          onClick={handleApplyCropAndUpload}
          disabled={uploading || !croppedAreaPixels}
        >
          {uploading ? 'Traitement...' : 'Appliquer et uploader'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ImageUploadCropDialog;
